const TEXT_ENTRY_FIELD_TYPES = new Set(['text', 'textarea']);

export const normalizeKey = (label = '') => label.toLowerCase().replace(/\s+/g, '_');
export const normalizeFieldKey = (label = '') => label.toLowerCase().replace(/\s+/g, '');

const getFieldConfigMap = (fieldDefinitions = []) =>
  Object.fromEntries(
    fieldDefinitions.map((field) => [
      normalizeFieldKey(field?.name || field?.label || ''),
      {
        label: field?.name || field?.label || '',
        type: field?.type,
      },
    ]),
  );

const isEmptyValue = (value) => {
  if (value === undefined || value === null) return true;

  if (typeof value === 'boolean') {
    return !value;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return String(value).trim() === '';
};

const shouldApplyRegexValidation = (fieldType) => {
  if (!fieldType) return true;
  return TEXT_ENTRY_FIELD_TYPES.has(fieldType);
};

const getRegexValue = (fieldKey, value) => {
  const trimmedValue = String(value ?? '').trim();
  return fieldKey === 'phonenumber' ? trimmedValue.replace(/\D/g, '') : trimmedValue;
};

export const validateFormFields = (
  fields = [],
  values = {},
  validationMap = {},
  fieldDefinitions = [],
) => {
  const errors = {};
  const safeValidationMap = validationMap || {};
  const fieldConfigMap = getFieldConfigMap(fieldDefinitions);

  fields.forEach((field) => {
    const fieldKey = normalizeFieldKey(field?.name || field);
    const fieldConfig = fieldConfigMap[fieldKey] || {};
    const value = values[fieldKey];

    if (isEmptyValue(value)) {
      errors[fieldKey] = `${fieldConfig.label || field?.name || field} is required`;
      return;
    }

    if (!shouldApplyRegexValidation(fieldConfig.type) || !safeValidationMap[fieldKey]) {
      return;
    }

    const [pattern, message] = safeValidationMap[fieldKey].split(':::');
    const cleanedPattern = pattern.replace(/\\\\/g, '\\');
    const regex = new RegExp(cleanedPattern);
    const input = getRegexValue(fieldKey, value);

    if (!regex.test(input)) {
      errors[fieldKey] = message || `Invalid ${fieldConfig.label || fieldKey}`;
    }
  });

  return errors;
};

const validationRegex = async () => {
  try {
    const res = await fetch('/api/validation', { cache: 'no-store' });

    if (!res.ok) {
      console.warn(`Validation endpoint returned ${res.status}, continuing without regex rules.`);
      return {};
    }

    const data = await res.json();

    if (!Array.isArray(data)) {
      console.warn(
        'Validation endpoint returned a non-array payload, continuing without regex rules.',
      );
      return {};
    }

    return Object.fromEntries(
      data
        .filter((item) => item?.text && item?.value)
        .map((item) => [item.text.toLowerCase(), item.value]),
    );
  } catch (error) {
    console.warn('Validation endpoint request failed, continuing without regex rules.', error);
    return {};
  }
};

let cachedValidationMap = null;
let promise = null;

export const getValidationMap = async () => {
  if (cachedValidationMap) return cachedValidationMap;

  if (!promise) {
    promise = validationRegex().then((data) => {
      cachedValidationMap = data;
      return data;
    });
  }

  return promise;
};
